import { useCallback } from 'react';

import isEqual from 'fast-deep-equal';
import { isEmpty } from 'radash';

import { isNotDefined, isOfType } from '@hsp/utilities';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import {
  useFieldValues,
  useGenerateSearchParameters,
  useIsValidFieldValues,
  useMultiRfm,
} from 'pages/audience/shared/hooks';
import { AudienceActions } from 'store/actions';
import {
  selectAudienceAggregate,
  selectAudienceAggregateApplied,
  selectAudienceAggregateLoading,
  selectAudienceAggregateTransformed,
} from 'store/selectors';
import { IMultiRfmSearch } from 'types/api/audience';

export const useAggregate = () => {
  const dispatch = useAppDispatch();

  const actual = useAppSelector(selectAudienceAggregate, isEqual);
  const applied = useAppSelector(selectAudienceAggregateApplied);
  const aggregate = useAppSelector(selectAudienceAggregateTransformed, isEqual);
  const loading = useAppSelector(selectAudienceAggregateLoading);

  const { fieldValues, isDefault } = useFieldValues();
  const generateSearchParameters = useGenerateSearchParameters();
  const isValid = useIsValidFieldValues();
  const { total: multiRfmTotal } = useMultiRfm();

  const reset = useCallback(() => dispatch(AudienceActions.clearAggregate()), [dispatch]);

  const apply = useCallback(() => {
    if (isNotDefined(fieldValues) || isEmpty(fieldValues) || (isDefault && multiRfmTotal < 2)) {
      reset();

      return;
    }

    if (!isValid) {
      return;
    }

    if (applied || loading) {
      return;
    }

    const parameters = generateSearchParameters();

    if (isNotDefined(parameters) || isEmpty(parameters)) {
      return;
    }

    const { page, pageSize, sort, ...pagingRemoved } = parameters;

    if (isOfType<IMultiRfmSearch>(pagingRemoved, 'rfmSets')) {
      dispatch(AudienceActions.getAggregateMultiRfm(pagingRemoved));

      return;
    }

    dispatch(AudienceActions.getAggregate(pagingRemoved));
  }, [applied, dispatch, fieldValues, generateSearchParameters, isDefault, isValid, loading, multiRfmTotal, reset]);

  return {
    actual,
    aggregate,
    apply,
    loading,
    reset,
  } as const;
};
